import React, { useRef } from 'react';

const DEFAULT_RENTAL_OPTIONS = ['Single Day', 'Multi-Day'];

export default function BookingForm({
  boat,
  boatName,
  rentalOptions,
  variant,
  heading = 'Tell Us Your Plan',
  submitLabel = 'Email Availability Request',
  showHeading = true,
  isPopup,
  className = '',
}) {
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const dateRef = useRef(null);
  const lengthRef = useRef(null);
  const messageRef = useRef(null);

  const effectiveVariant = variant || (isPopup ? 'modal' : 'section');

  const resolvedBoatName = boat?.name || boatName || '';
  const formIdBase = boat?.id ? `boat-${boat.id}` : 'booking';

  let resolvedRentalOptions = DEFAULT_RENTAL_OPTIONS;

  if (Array.isArray(rentalOptions) && rentalOptions.length > 0) {
    resolvedRentalOptions = rentalOptions;
  } else if (Array.isArray(boat?.rentalOptions) && boat.rentalOptions.length > 0) {
    resolvedRentalOptions = boat.rentalOptions;
  } else if (Array.isArray(boat?.rentalLengths) && boat.rentalLengths.length > 0) {
    resolvedRentalOptions = boat.rentalLengths;
  }

  const preferredRentalOption = boat?.defaultRentalOption;
  const defaultRentalOption = preferredRentalOption && resolvedRentalOptions.includes(preferredRentalOption)
    ? preferredRentalOption
    : resolvedRentalOptions[0];

  const variantStyles = effectiveVariant === 'modal'
    ? {
        container: ['booking-form', 'w-100', 'text-dark'],
        heading: 'text-dark fw-semibold',
        input: 'form-control',
        select: 'form-select',
        label: 'text-muted',
        button: 'btn btn-primary w-100 py-3',
      }
    : {
        container: ['booking-form', 'text-white'],
        heading: 'text-white',
        input: 'form-control bg-transparent text-white border-light',
        select: 'form-select bg-transparent text-white border-light',
        label: 'text-white',
        button: 'btn btn-outline-light w-100 py-3',
      };

  const handleSubmit = (e) => {
    e.preventDefault();

    const name = nameRef.current?.value || '';
    const email = emailRef.current?.value || '';
    const date = dateRef.current?.value || '';
    const rentalLength = lengthRef.current?.value || defaultRentalOption || '';
    const notes = messageRef.current?.value || '';

    const subjectBase = resolvedBoatName ? `Rental Request for ${resolvedBoatName}` : 'Rental Availability Request';
    const subject = rentalLength ? `${subjectBase} - ${rentalLength}` : subjectBase;
    const body = [
      `Name: ${name}`,
      `Email: ${email}`,
      `Preferred Date/Time: ${date}`,
      `Rental Length: ${rentalLength}`,
      resolvedBoatName ? `Boat: ${resolvedBoatName}` : null,
      '',
      'Notes / Special Requests:',
      notes
    ].filter(Boolean).join('\n');

    window.location.href = `mailto:info@sunbayboats.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const fieldColumnClass = effectiveVariant === 'modal' ? 'col-12' : 'col-md-6';

  const formContent = (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        <div className={fieldColumnClass}>
          <div className="form-floating">
            <input
              type="text"
              className={variantStyles.input}
              id={`${formIdBase}-name`}
              placeholder="Your Name"
              ref={nameRef}
              autoComplete="name"
              required
            />
            <label className={variantStyles.label} htmlFor={`${formIdBase}-name`}>Your Name</label>
          </div>
        </div>

        <div className={fieldColumnClass}>
          <div className="form-floating">
            <input
              type="email"
              className={variantStyles.input}
              id={`${formIdBase}-email`}
              placeholder="Your Email"
              ref={emailRef}
              autoComplete="email"
              required
            />
            <label className={variantStyles.label} htmlFor={`${formIdBase}-email`}>Your Email</label>
          </div>
        </div>

        <div className={fieldColumnClass}>
          <div className="form-floating">
            <input
              type="text"
              className={variantStyles.input}
              id={`${formIdBase}-datetime`}
              placeholder="Preferred Date & Time"
              ref={dateRef}
            />
            <label className={variantStyles.label} htmlFor={`${formIdBase}-datetime`}>Preferred Date &amp; Time</label>
          </div>
        </div>

        <div className={fieldColumnClass}>
          <div className="form-floating">
            <select
              className={variantStyles.select}
              id={`${formIdBase}-rentalLength`}
              ref={lengthRef}
              defaultValue={defaultRentalOption}
            >
              {resolvedRentalOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <label className={variantStyles.label} htmlFor={`${formIdBase}-rentalLength`}>Rental Length</label>
          </div>
        </div>

        <div className="col-12">
          <div className="form-floating">
            <textarea
              className={variantStyles.input}
              placeholder="Notes / Special Requests (pickup location, times, captain request, etc.)"
              id={`${formIdBase}-message`}
              style={{ height: 100 }}
              ref={messageRef}
              defaultValue=""
            />
            <label className={variantStyles.label} htmlFor={`${formIdBase}-message`}>Notes / Special Requests</label>
          </div>
        </div>

        <div className="col-12">
          <button
            className={variantStyles.button}
            type="submit"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );

  const HeadingTag = effectiveVariant === 'modal' ? 'h4' : 'h1';

  if (effectiveVariant === 'modal') {
    const containerClasses = [...variantStyles.container, className].filter(Boolean).join(' ');

    return (
      <div className={containerClasses}>
        {showHeading && (
          <HeadingTag className={`${variantStyles.heading} mb-4`}>{heading}</HeadingTag>
        )}
        {formContent}
      </div>
    );
  }

  return (
    <div className={`col-md-6 ${className}`.trim()}>
      {showHeading && (
        <HeadingTag className={`${variantStyles.heading} mb-4`}>{heading}</HeadingTag>
      )}
      {formContent}
    </div>
  );
}
